import { visit } from 'unist-util-visit';
import type { Plugin } from 'unified';
import type { Html, Parent, Root, Text } from 'mdast';

// Matches any <citation ...> opening tag, with or without the self-closing
// slash (a plausible model formatting slip). source-id is extracted
// separately so extra attributes the model invents (e.g. relevance="0.87",
// echoed from the <chunk> tags in its prompt) don't break matching.
// source-id is a source document uuid, so no attribute escaping is needed.
const CITATION_TAG = /^<citation\b[^>]*?\/?>$/i;
const CITATION_SOURCE_ID = /\bsource-id="([^"]*)"/i;
const CITATION_CLOSING_TAG = /^<\/citation>$/i;

/**
 * Turns <citation source-id="..."/> tags the model writes inline into custom
 * nodes rendered as <sup data-citation-source-id="..." data-citation-number="N">
 * elements, which ChatMessage intercepts via its markdown components map to
 * resolve the source-id against message.groundingChunks (already loaded for
 * the Sources drawer) and show the document's title.
 *
 * The number is purely a client-side display convenience — assigned by first
 * appearance within this one render, reusing the same number for a repeated
 * source-id — nothing is persisted or fetched from the backend for it.
 *
 * remark parses tag-shaped text as "html" nodes (not "text"), so this visits
 * "html" nodes rather than doing a text-based regex pass. A bare closing
 * </citation> (left over if the model didn't self-close) is dropped
 * silently; any other unrecognised raw HTML is turned back into literal
 * text so it doesn't silently vanish (react-markdown drops raw HTML nodes
 * by default).
 *
 * A citation whose source-id already appears in the immediately preceding
 * run of citations (separated only by whitespace) is dropped along with the
 * whitespace, so the model repeating a citation back-to-back renders one chip.
 */
export const remarkCitations: Plugin<[], Root> = () => (tree: Root): void => {
  const numberBySourceId = new Map<string, number>();

  const citationSourceId = (node: Parent['children'][number]): string | null => {
    const n = node as unknown as {
      type: string;
      data?: { hProperties?: Record<string, string> };
    };

    return n.type === 'citation'
      ? (n.data?.hProperties?.['data-citation-source-id'] ?? null)
      : null;
  };

  const isBlank = (node: Parent['children'][number]): boolean =>
    node.type === 'text' && node.value.trim() === '';

  /** Index just after the nearest preceding citation if `sourceId` already
   * appears in the contiguous citation run before `index`, else null. */
  const duplicateRunStart = (
    parent: Parent,
    index: number,
    sourceId: string
  ): number | null => {
    let nearestCitation: number | null = null;
    let seen = false;

    for (let i = index - 1; i >= 0; i--) {
      const sibling = parent.children[i];

      if (!sibling || isBlank(sibling)) continue;

      const siblingSourceId = citationSourceId(sibling);

      if (siblingSourceId === null) break;

      nearestCitation ??= i;

      seen ||= siblingSourceId === sourceId;
    }

    return seen && nearestCitation !== null ? nearestCitation + 1 : null;
  };

  visit(tree, 'html', (node: Html, index, parent) => {
    if (!parent || index === undefined) return undefined;

    if (CITATION_CLOSING_TAG.test(node.value)) {
      parent.children.splice(index, 1);

      return index;
    }

    if (!CITATION_TAG.test(node.value)) {
      const fallback: Text = { type: 'text', value: node.value };

      parent.children.splice(index, 1, fallback);

      return undefined;
    }

    const sourceId = CITATION_SOURCE_ID.exec(node.value)?.[1];

    // A citation tag with no usable source-id is model noise — showing the
    // raw tag to the user is worse than showing nothing.
    if (!sourceId) {
      parent.children.splice(index, 1);

      return index;
    }

    const runStart = duplicateRunStart(parent, index, sourceId);

    if (runStart !== null) {
      parent.children.splice(runStart, index - runStart + 1);

      return runStart;
    }

    const number = numberBySourceId.get(sourceId) ?? numberBySourceId.size + 1;

    numberBySourceId.set(sourceId, number);

    parent.children.splice(index, 1, {
      type: 'citation',
      data: {
        hName: 'sup',
        hProperties: {
          'data-citation-source-id': sourceId,
          'data-citation-number': String(number),
        },
        hChildren: [{ type: 'text', value: `[${number}]` }],
      },
    } as unknown as Text);

    return undefined;
  });
};
